import React, { useState, useEffect } from 'react';

interface AvatarProps {
  /** URL del avatar. Si es vacío/null o la imagen falla, muestra iniciales. */
  src?: string | null;
  /** Nombre para derivar las iniciales del fallback. */
  name?: string | null;
  /** Tamaño en px (cuadrado). Default 40. */
  size?: number;
  /** Color de fondo del fallback. */
  bg?: string;
  style?: React.CSSProperties;
  className?: string;
  alt?: string;
}

function getInitials(name?: string | null): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('') || '?';
}

/**
 * Avatar resiliente: renderiza la imagen y, si falla la carga (URL rota, 404,
 * etc.), cae automáticamente a las iniciales en vez de mostrar el ícono roto.
 */
export const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  size = 40,
  bg = '#333',
  style,
  className,
  alt,
}) => {
  const [failed, setFailed] = useState(false);

  // Si cambia el src (ej: el usuario sube otra foto), reseteamos el estado de error.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  const base: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
    ...style,
  };

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt ?? name ?? ''}
        className={className}
        style={base}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div
      className={className}
      style={{
        ...base,
        objectFit: undefined,
        background: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#bbb',
        fontWeight: 700,
        fontSize: Math.max(10, Math.round(size * 0.4)),
        lineHeight: 1,
        userSelect: 'none',
      }}
      aria-label={name ?? undefined}
    >
      {getInitials(name)}
    </div>
  );
};

export default Avatar;
